import { useEffect, useState } from "react";
import type { Session, User, SupabaseClient } from "@supabase/supabase-js";

// Lazy-loaded reference shared across all useAuth() consumers, so we only
// pay the Supabase JS bundle cost (auth-js + realtime + storage) once and
// only AFTER hydration. Keeping it out of the eager import graph removes
// ~30-40 KiB of unused JS from the main bundle on pages that don't need auth.
let supabasePromise: Promise<SupabaseClient> | null = null;
function loadSupabase(): Promise<SupabaseClient> {
  if (!supabasePromise) {
    supabasePromise = import("@/integrations/supabase/client").then((m) => m.supabase);
  }
  return supabasePromise;
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    loadSupabase().then((supabase) => {
      if (cancelled) return;
      const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
        setSession(s);
        setUser(s?.user ?? null);
      });
      unsubscribe = () => sub.subscription.unsubscribe();
      supabase.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return {
    session,
    user,
    loading,
    signOut: async () => {
      const supabase = await loadSupabase();
      return supabase.auth.signOut();
    },
  };
}
